import { create } from 'zustand'
import { persist } from 'zustand/middleware'

import { authService } from '@/services'
import { useAuthStore } from '@/stores/authStore'
import { QRCodeStatus } from '@/types/qrcode'
import { generateQRCode } from '@/utils/qrcode'

interface QrcodeState {
  loading: boolean
  generating: boolean
  qrValue: string
  qrImage: string
  error: boolean
  errorMessage: string
  timeLeft: number
  intervalRef: NodeJS.Timeout | null
  pollIntervalRef: NodeJS.Timeout | null
  qrcodeStatus: string
}

interface QrcodeActions {
  initialize: (state: QrcodeState) => void
  openLoading: () => void
  closeLoading: () => void
  openGenerating: () => void
  closeGenerating: () => void
  saveQRValue: (value: string) => void
  saveQRImage: (value: string) => void
  clearQRImage: () => void
  generateQRImage: () => void
  getQRCode: () => Promise<void>
  setError: () => void
  updateErrorMessage: (errorMessage: string) => void
  updateTimeLeft: (value: number) => void
  setIntervalRef: (value: NodeJS.Timeout | null) => void
  setPollIntervalRef: (value: NodeJS.Timeout | null) => void
  startCountdown: (value: number) => void
  startPolling: (value: string) => void
  checkLoginStatus: (value: string) => Promise<boolean>
  clearTimers: () => void
  updateQRCodeStatus: (value: string) => void
}

type QrcodeStore = QrcodeState & QrcodeActions

// 初始状态
export const initialState: QrcodeState = {
  loading: true,
  generating: false,
  qrValue: '',
  qrImage: '',
  error: false,
  errorMessage: '',
  timeLeft: 0,
  intervalRef: null,
  pollIntervalRef: null,
  qrcodeStatus: QRCodeStatus.PENDING
}

const COUNT_DOWN_INTERVAL_SEC = 1
const POLLING_INTERVAL_SEC = 3

export const useQrcodeStore = create<QrcodeStore>()(
  persist(
    (set, get) => ({
      ...initialState,
      initialize: (state) => set({ ...state }),
      openLoading: () => set({ loading: true }),
      closeLoading: () => set({ loading: false }),
      openGenerating: () => set({ generating: true }),
      closeGenerating: () => set({ generating: false }),
      saveQRValue: (value) => set({ qrValue: value }),
      saveQRImage: (value) => set({ qrImage: value }),
      clearQRImage: () => set({ qrImage: '' }),
      setError: () => set({ error: true }),
      updateErrorMessage: (errorMessage) => set({ errorMessage: errorMessage }),
      updateTimeLeft: (value) => set({ timeLeft: value }),
      setIntervalRef: (value) => set({ intervalRef: value }),
      setPollIntervalRef: (value) => set({ pollIntervalRef: value }),
      updateQRCodeStatus: (value) => set({ qrcodeStatus: value }),

      // 开始倒计时
      startCountdown: (expireTime: number) => {
        get().setIntervalRef(
          setInterval(() => {
            const remainingTime = Math.max(0, expireTime - Date.now())
            const seconds = Math.floor(remainingTime / 1000)
            get().updateTimeLeft(seconds)

            if (seconds <= 0) {
              get().updateQRCodeStatus(QRCodeStatus.EXPIRED)
              get().clearTimers()
            }
          }, COUNT_DOWN_INTERVAL_SEC * 1000)
        )
      },

      // 开始轮训
      startPolling: (loginKey: string) => {
        get().clearTimers()
        get().setPollIntervalRef(
          setInterval(async () => {
            const success = await get().checkLoginStatus(loginKey)
            if (success) {
              get().clearTimers()
            }
          }, POLLING_INTERVAL_SEC * 1000)
        )
      },

      // 清楚所有定时器
      clearTimers: () => {
        const intervalRef = get().intervalRef
        if (intervalRef !== null) {
          clearInterval(intervalRef)
          get().setIntervalRef(null)
        }
        const pollIntervalRef = get().pollIntervalRef
        if (pollIntervalRef !== null) {
          clearInterval(pollIntervalRef)
          get().setPollIntervalRef(null)
        }
      },

      // 创建二维码图片
      generateQRImage: async () => {
        try {
          get().openGenerating()
          get().clearQRImage()

          const qrValue = get().qrValue
          if (qrValue) {
            // 检查是否已经是base64格式的图片
            if (qrValue.startsWith('data:image/')) {
              get().saveQRImage(qrValue)
            } else {
              // 将URL转换为二维码
              const qrImage = await generateQRCode(qrValue, {
                width: 200,
                margin: 2
              })
              get().saveQRImage(qrImage)
            }
          }
        } catch (error) {
          console.error('生成二维码失败:', error)
          get().clearQRImage()
        } finally {
          get().closeGenerating()
        }
      },
      // 获取二维码数据
      getQRCode: async () => {
        try {
          get().openLoading()
          const response = await authService.getQRCode()
          if (response.success) {
            get().saveQRValue(response.data.qrCode)

            // 计算剩余时间
            const remainingTime = Math.max(
              0,
              response.data.expireTime - Date.now()
            )
            get().updateTimeLeft(Math.floor(remainingTime / 1000))

            // 开始轮询状态
            get().startPolling(response.data.loginKey)

            // 开始倒计时
            get().startCountdown(response.data.expireTime)
          }
        } catch (error: unknown) {
          get().setError()
          const errorMessage =
            error instanceof Error ? error.message : '获取二维码失败'
          get().updateErrorMessage(errorMessage)
        } finally {
          get().closeLoading()
        }
      },

      // 检查登录状态
      checkLoginStatus: async (loginKey: string) => {
        try {
          const response = await authService.checkQRCodeStatus(loginKey)

          if (response.success) {
            const {
              status: newStatus,
              token,
              refreshToken,
              user
            } = response.data

            get().updateQRCodeStatus(newStatus)

            if (
              newStatus === QRCodeStatus.CONFIRMED &&
              token &&
              refreshToken &&
              user
            ) {
              console.log(user)
              // 登录成功
              useAuthStore.getState().setAuth(token, refreshToken, user)
              return true
            } else if (
              newStatus === QRCodeStatus.CANCELED ||
              newStatus === QRCodeStatus.EXPIRED
            ) {
              // 取消或过期
              get().clearTimers()
            }
          }

          return false
        } catch (error: Error | unknown) {
          console.error('Check login status error:', error)
          return false
        }
      }
    }),
    {
      name: 'auth-qrcode-storage'
    }
  )
)

export const useQRCodeState = () => {
  const QRValue = useQrcodeStore((state) => state.qrValue)
  return { QRValue }
}

export const useQrcodeActions = () => {
  const initialize = useQrcodeStore((state) => state.initialize)
  const generateQRImage = useQrcodeStore((state) => state.generateQRImage)
  const getQRCode = useQrcodeStore((state) => state.getQRCode)
  const clearTimers = useQrcodeStore((state) => state.clearTimers)
  return {
    initialize,
    generateQRImage,
    getQRCode,
    clearTimers
  }
}
